import { useContext, useEffect, useState } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useOutcomesData } from 'hooks/useOutcomesData';
import { exportCsv } from 'lib/exportCsv';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Package, ShoppingCart, MapPin, Info, TrendingUp, AlertTriangle, Download } from 'lucide-react';

/**
 * Outcomes — did conversations lead anywhere?
 *
 * Every panel here was measured before it was written. The one the mock asks
 * for and this does NOT build is conversion rate; see the note on that card.
 */

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** Headline number. Never renders a null as 0. */
function Stat({ label, value, sub, note }) {
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-medium">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
      {/* `note` carries a caveat about what the number DOES NOT include. Kept
          separate from `sub` (which describes the number itself) so the two
          cannot be confused, and dimmer so it reads as a footnote. */}
      {note && <div className="text-[11px] text-amber-700 dark:text-amber-500 mt-1.5">{note}</div>}
    </div>
  );
}

/** A metric with no honest source. Dimmed, labelled, reason stated. */
function PendingStat({ label, badge, reason }) {
  return (
    <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-4 opacity-70">
      <div className="text-xs text-gray-500 mb-1 flex items-center gap-2">
        {label}
        <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 whitespace-nowrap">
          {badge}
        </Badge>
      </div>
      <div className="text-2xl font-medium text-gray-400">—</div>
      <div className="text-xs text-gray-500 mt-1">{reason}</div>
    </div>
  );
}

export default function OutcomesTab() {
  const { selectedClient, dateRange, platformFilter } = useContext(AppContext);
  const {
    summary, summaryLoading, summaryError, fetchSummary,
    shown, added, productsLoading, fetchProducts,
    entryPoints, entryLoading, entryError, fetchEntryPoints,
  } = useOutcomesData();

  // Off by default: the tab describes ALL traffic unless you say otherwise, and
  // the amber note tells you how much of it is internal. Defaulting to ON would
  // silently change what every figure means.
  const [audience, setAudience] = useState('all');
  const excludeInternal = audience === 'customers';
  const internalOnly = audience === 'internal';

  useEffect(() => {
    fetchSummary({ excludeInternal, internalOnly });
    fetchProducts({ excludeInternal, internalOnly });
    fetchEntryPoints({ excludeInternal, internalOnly });
  }, [selectedClient, dateRange, audience, excludeInternal, internalOnly, platformFilter,
      fetchSummary, fetchProducts, fetchEntryPoints]);

  // A "ranking" whose leader is in single digits is noise wearing a table's
  // clothes. Measured over 13 days the top product had 9 impressions against
  // 662 distinct products -- a 9-vs-8 gap is not a finding, but it reads like
  // one. Warn ABOVE the table, where it is read before the numbers are.
  const topImpressions = shown.length ? shown[0].impressions : 0;
  const rankingIsThin = shown.length > 0 && topImpressions < 15;

  return (
    <div className="space-y-5">

      {/* ── Audience filter ────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        {[
          { v: 'all', l: 'All Traffic' },
          { v: 'customers', l: 'Customers Only' },
          { v: 'internal', l: 'Internal Only' },
        ].map((o) => (
          <Button key={o.v} size="sm"
                  variant={audience === o.v ? 'default' : 'outline'}
                  onClick={() => setAudience(o.v)}>
            {o.l}
          </Button>
        ))}
        {/* The old copy said "plus local and staging origins" for the whole
            toggle. That is only true of Assistant opened: it filters on
            page_url, which the cart and message tables do not record, so those
            two cards can exclude the 26 accounts and nothing more. Measured
            2026-08-19: on landscape NOT ONE cart add carries an origin, so we
            cannot even say how much test traffic is in that figure. Claiming
            the exclusion happened everywhere is what made a reader compare the
            bubble card's -71% against the cart card's -20% and conclude the
            cart number was wrong. It was not; the label was. */}
        <span className="text-xs text-gray-500">
          {audience === 'customers'
            ? 'Excluding internal accounts. Assistant opened also excludes local and staging origins — cart and impressions cannot, so test traffic from those may remain.'
            : audience === 'internal'
              ? 'Internal accounts only. Local and staging origins stay visible — internal traffic is exactly what this view is for.'
              : 'Including internal accounts, local and staging.'}
        </span>
      </div>

      {/* ── Headline ───────────────────────────────────────────────────── */}
      {summaryError && <div className="text-sm text-red-600">{summaryError}</div>}
      {summaryLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}

      {!summaryLoading && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Stat
              label="Product Impressions"
              value={fmt(summary.impressions)}
              sub={`${fmt(summary.distinct_products_shown)} distinct products`}
              note={excludeInternal ? 'Internal accounts only — origin not recorded' : undefined}
            />
            <Stat
              label="Avg Product Price Shown"
              value={money(summary.avg_net_price)}
              sub="net price, from the catalog"
            />
            <Stat
              label="Added to Cart"
              value={fmt(summary.adds)}
              sub={`${fmt(summary.distinct_products_added)} distinct products`}
              note={excludeInternal ? 'Internal accounts only — origin not recorded' : undefined}
            />
            <Stat
              label="Assistant Opened"
              value={fmt(summary.bubble_displays)}
              sub={`across ${fmt(summary.distinct_entry_pages)} pages — widget opens`}
            />
            <PendingStat
              label="Conversion Rate"
              badge="Can't Be Measured"
              reason="Products shown and products added use different SKU formats, so they can't be matched reliably."
            />
          </div>

          {summary.platform && (
            <div className="flex gap-2 rounded-md border border-zinc-300 dark:border-zinc-700 p-2.5 text-xs text-gray-600 dark:text-gray-300">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                The <strong>{summary.platform}</strong> filter applies to product impressions only.
                Add-to-cart and widget opens carry no platform, so those figures still cover
                everything — they are not filtered rather than filtered to nothing.
              </span>
            </div>
          )}

          {summary.environment === 'prod' && (
            <div className="text-xs text-gray-500">
              Production view: staging-labeled traffic is excluded from impressions and widget
              opens. The add-to-cart table records no environment, so that figure still
              includes whatever staging traffic reached it.
            </div>
          )}

          {!excludeInternal && summary.internal_add_pct !== null && summary.internal_add_pct > 0 && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>{summary.internal_add_pct}%</strong> of add-to-carts
                ({fmt(summary.adds_from_internal)} of {fmt(summary.adds)}) come from internal
                accounts, and widget opens include local and staging origins. These are counted,
                not filtered — the figures above describe all traffic. Switching to Customers only
                removes the internal accounts everywhere, but local and staging origins only from
                widget opens.
              </span>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

        {/* ── Products shown ───────────────────────────────────────────── */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Products Shown Most
              {shown.length > 0 && (
                <Button size="sm" variant="outline" className="ml-auto font-normal"
                        onClick={() => exportCsv('products-shown', shown)}>
                  <Download className="h-3.5 w-3.5 mr-1" />CSV
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {productsLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
            {!productsLoading && shown.length === 0 && (
              <div className="text-sm text-gray-500 py-6">No product cards shown in this range.</div>
            )}
            {!productsLoading && rankingIsThin && (
              <div className="flex gap-2 mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Too little data to rank. The most-shown product appears just{' '}
                  <strong>{topImpressions}</strong> times, so the order below is close to
                  chance. Widen the date range before reading anything into it.
                </span>
              </div>
            )}
            {!productsLoading && shown.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left font-normal py-1.5">Part Number</th>
                    <th className="text-right font-normal">Times Shown</th>
                    <th className="text-right font-normal">Avg Price</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.part_number} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-1.5 pr-2 font-mono text-xs truncate max-w-[200px]" title={r.part_number}>
                        {r.part_number}
                      </td>
                      <td className="text-right tabular-nums">{r.impressions}</td>
                      <td className="text-right tabular-nums text-gray-500">{money(r.avg_net_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="pt-3 text-xs text-gray-500 flex gap-2">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                A very long tail — most products are shown once or twice, so the leaders here
                have small counts. Read it as a list, not a ranking with a clear winner.
              </span>
            </p>
          </CardContent>
        </Card>

        {/* ── Products added ───────────────────────────────────────────── */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Products Added to Cart
            </CardTitle>
          </CardHeader>
          <CardContent>
            {productsLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
            {!productsLoading && added.length === 0 && (
              <div className="text-sm text-gray-500 py-6">Nothing added to cart in this range.</div>
            )}
            {!productsLoading && added.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left font-normal py-1.5">Product</th>
                    <th className="text-right font-normal">Adds</th>
                    <th className="text-right font-normal">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {added.map((r, i) => (
                    <tr key={r.product_name || i} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-1.5 pr-2 truncate max-w-[260px]" title={r.product_name}>
                        {r.product_name}
                      </td>
                      <td className="text-right tabular-nums font-medium">{r.adds}</td>
                      <td className="text-right tabular-nums text-gray-500">{fmt(r.total_qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="pt-3 text-xs text-gray-500 flex gap-2">
              <TrendingUp className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                These are the cart's own product names, which is why they read differently from
                the part numbers on the left. The two lists cannot be matched up — see the
                conversion-rate note above.
              </span>
            </p>
          </CardContent>
        </Card>

        {/* ── Entry points ─────────────────────────────────────────────── */}
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Where Users Open the Assistant
              {entryPoints.length > 0 && (
                <Button size="sm" variant="outline" className="ml-auto font-normal"
                        onClick={() => exportCsv('entry-points', entryPoints)}>
                  <Download className="h-3.5 w-3.5 mr-1" />CSV
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {entryLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}

            {/* Error BEFORE empty, and they say different things. "No opens
              * recorded" is a claim about the business; if the request failed
              * we know nothing about the business and must not make one. */}
            {!entryLoading && entryError && (
              <div className="text-sm py-6 text-amber-700 dark:text-amber-300">
                Couldn't load entry points — the figure below is not "zero", it is unknown.
                <span className="block text-xs text-gray-500 mt-1">{entryError}</span>
              </div>
            )}
            {!entryLoading && !entryError && entryPoints.length === 0 && (
              <div className="text-sm text-gray-500 py-6">No widget opens recorded in this range.</div>
            )}
            {!entryLoading && entryPoints.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left font-normal py-1.5">Page</th>
                    <th className="text-right font-normal">Opens</th>
                  </tr>
                </thead>
                <tbody>
                  {entryPoints.map((r, i) => (
                    <tr key={r.page_url || i} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-1.5 pr-2 truncate max-w-[520px]" title={r.page_url}>
                        {r.page_url}
                        {r.is_internal_origin && (
                          <Badge variant="outline" className="ml-2 font-normal text-[10px] px-1.5 py-0">
                            local / staging
                          </Badge>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{r.opens}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
